import { Graphics } from 'pixi.js';
import { bindKeys } from '../input/InputController';
import { TRACK_COLORS } from '../render/colors';
import { VIRTUAL_H, VIRTUAL_W } from '../render/StageScaler';
import {
  LEAGUE_DEFS,
  equilibriumPace,
  makeRaceSetup,
  statsToParams,
  sustainableTapRate,
  theoreticalBestTime,
} from '../sim/race/params';
import type { HorseStats, StatId } from '../sim/race/types';
import { el } from '../ui/dom';
import { RaceScene } from './RaceScene';
import type { Scene, SceneContext } from './SceneManager';

const STAT_IDS: StatId[] = ['speed', 'stamina', 'accel', 'heart'];
const STAT_LABELS: Record<StatId, string> = {
  speed: 'Speed',
  stamina: 'Stamina',
  accel: 'Acceleration',
  heart: 'Heart',
};

/**
 * Phase 1's whole game: pick stats, pick a league, race rectangles.
 * Stays reachable behind ?graybox forever once the ranch exists.
 */
export class GrayboxScene implements Scene {
  private readonly initialSeed: number | null;
  private ctx!: SceneContext;
  private panel!: HTMLElement;
  private readout!: HTMLElement;
  private backdrop: Graphics | null = null;
  private unbind: (() => void) | null = null;
  private readonly sliders = new Map<StatId, HTMLInputElement>();
  private readonly sliderValues = new Map<StatId, HTMLElement>();
  private leagueSelect!: HTMLSelectElement;
  private distanceSelect!: HTMLSelectElement;
  private seedInput!: HTMLInputElement;

  constructor(initialSeed: number | null) {
    this.initialSeed = initialSeed;
  }

  enter(ctx: SceneContext): void {
    this.ctx = ctx;

    this.backdrop = new Graphics()
      .rect(0, 0, VIRTUAL_W, VIRTUAL_H)
      .fill(TRACK_COLORS.sky)
      .rect(0, 170, VIRTUAL_W, 12)
      .fill(TRACK_COLORS.skylineHill)
      .rect(0, 182, VIRTUAL_W, VIRTUAL_H - 182)
      .fill(TRACK_COLORS.apron);
    ctx.scaler.root.addChild(this.backdrop);

    const sliderRows = STAT_IDS.map((stat) => {
      const valueLabel = el('span', { class: 'slider-value' }, '4');
      const slider = el('input', {
        type: 'range',
        min: '1',
        max: '20',
        value: '4',
        oninput: () => {
          valueLabel.textContent = slider.value;
          this.refreshReadout();
        },
      });
      this.sliders.set(stat, slider);
      this.sliderValues.set(stat, valueLabel);
      return el('label', { class: 'slider-row' }, el('span', { class: 'slider-name' }, STAT_LABELS[stat]), slider, valueLabel);
    });

    this.leagueSelect = el(
      'select',
      { onchange: () => this.refreshReadout() },
      ...LEAGUE_DEFS.map((def, index) =>
        el('option', { value: String(index) }, `${def.name} — ${def.distanceM}m vs L${def.medianLevel}`),
      ),
    );

    this.distanceSelect = el(
      'select',
      { onchange: () => this.refreshReadout() },
      el('option', { value: 'auto' }, 'League distance'),
      el('option', { value: '300' }, '300 m'),
      el('option', { value: '400' }, '400 m'),
      el('option', { value: '500' }, '500 m'),
    );

    this.seedInput = el('input', {
      type: 'number',
      placeholder: 'random',
      value: this.initialSeed !== null ? String(this.initialSeed) : '',
    });

    this.readout = el('div', { class: 'graybox-readout' });

    this.panel = el(
      'div',
      { class: 'panel graybox-panel interactive' },
      el('div', { class: 'graybox-title' }, 'DERBY DAYS'),
      el('div', { class: 'graybox-sub' }, 'race lab · gray-box'),
      ...sliderRows,
      el('label', { class: 'select-row' }, el('span', {}, 'League'), this.leagueSelect),
      el('label', { class: 'select-row' }, el('span', {}, 'Distance'), this.distanceSelect),
      el('label', { class: 'select-row' }, el('span', {}, 'Seed'), this.seedInput),
      this.readout,
      el('button', { class: 'button primary', onclick: () => this.startRace() }, 'Race! (Enter)'),
      el('div', { class: 'graybox-help' }, 'SPACE taps · tap near the gun for a perfect break · pace the tank'),
    );
    ctx.uiRoot.append(this.panel);

    this.unbind = bindKeys([
      { code: 'Enter', handler: () => this.startRace() },
      {
        code: 'Escape',
        handler: () => {
          // Pushed from the title screen: Escape goes back. As the root
          // scene (?graybox) there is nowhere to go.
          if (ctx.scenes.depth > 1) ctx.scenes.pop();
        },
      },
    ]);
    this.refreshReadout();
  }

  frame(): void {
    // Static scene; nothing advances.
  }

  suspend(): void {
    this.panel.style.display = 'none';
    if (this.backdrop) this.backdrop.visible = false;
  }

  resume(): void {
    this.panel.style.display = '';
    if (this.backdrop) this.backdrop.visible = true;
  }

  exit(): void {
    this.unbind?.();
    this.panel.remove();
    this.backdrop?.destroy();
    this.backdrop = null;
  }

  private currentStats(): HorseStats {
    const read = (stat: StatId): number => Number(this.sliders.get(stat)?.value ?? 4);
    return { speed: read('speed'), stamina: read('stamina'), accel: read('accel'), heart: read('heart') };
  }

  private currentDistance(): number | undefined {
    const raw = this.distanceSelect.value;
    return raw === 'auto' ? undefined : Number(raw);
  }

  private refreshReadout(): void {
    const league = LEAGUE_DEFS[Number(this.leagueSelect.value)] ?? LEAGUE_DEFS[0]!;
    const distance = this.currentDistance() ?? league.distanceM;
    const player = statsToParams(this.currentStats());
    const rival = statsToParams({
      speed: league.medianLevel,
      stamina: league.medianLevel,
      accel: league.medianLevel,
      heart: league.medianLevel,
    });
    this.readout.textContent =
      `you: ${sustainableTapRate(player).toFixed(1)} taps/s · cruise ${equilibriumPace(player).toFixed(1)} m/s · ` +
      `ceiling ${theoreticalBestTime(player, distance).toFixed(1)}s\n` +
      `field: cruise ${equilibriumPace(rival).toFixed(1)} m/s over ${distance}m`;
  }

  private startRace(): void {
    const league = LEAGUE_DEFS[Number(this.leagueSelect.value)] ?? LEAGUE_DEFS[0]!;
    const seedRaw = this.seedInput.value.trim();
    const seed = seedRaw === '' ? Date.now() >>> 0 : Number(seedRaw) >>> 0;
    const setup = makeRaceSetup(league, this.currentStats(), seed, {
      distanceM: this.currentDistance(),
    });
    this.ctx.scenes.push(new RaceScene(setup));
  }
}
